import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import React from "react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export const WEATHER_CACHE_MS = 15 * 60 * 1000;

export const WEATHER_ICON_FILES = {
    sunny: "sunny.svg",
    "partly-cloudy": "partly-cloudy.svg",
    cloudy: "cloudy.svg",
    "light-rain": "light-rain.svg",
    "heavy-rain": "heavy-rain.svg",
    snow: "snow.svg",
    hail: "hail.svg",
};

export const SPOT_TABS = [
    { id: "main", label: "Основное" },
    { id: "photos", label: "Фото" },
    { id: "reports", label: "Отчёты" },
    { id: "weather", label: "Погода + Клёв" },
    { id: "routes", label: "Маршруты" },
    { id: "notes", label: "Заметки" },
];

export function getVisibleSpotTabs({ includeNotes = false } = {}) {
    return includeNotes ? SPOT_TABS : SPOT_TABS.filter((tab) => tab.id !== "notes");
}

export function SpotBoolIcon({ value }) {
    return value
        ? <FontAwesomeIcon icon={faCheck} style={{ color: "var(--primary)" }} />
        : <FontAwesomeIcon icon={faXmark} style={{ color: "#fe1919" }} />;
}

export function fishingMethodLabel(spot) {
    if (spot?.fishingFromBoat && spot?.fishingFromShore) return "С берега и с лодки";
    if (spot?.fishingFromBoat) return "С лодки";
    if (spot?.fishingFromShore) return "С берега";
    return "-";
}

export function resolveSpotImage(spot) {
    const path = spot?.placePhotos?.[0]
        || spot?.photos?.[0]
        || spot?.mainPhoto
        || spot?.photo
        || spot?.image
        || spot?.place?.image
        || "";

    if (!path || /^(https?:)?\/\//.test(path) || path.startsWith("data:")) {
        return path;
    }

    return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

export function toSafeArray(value) {
    return Array.isArray(value) ? value : [];
}

export function toDateTimeLocalValue(date) {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
}

export function commentFieldError(message) {
    return message ? <span className="new-spot-field-error">{message}</span> : null;
}

export function formatWindDirection(value) {
    if (value === null || value === undefined || value === "") return "-";
    const mapByIndex = [
        "С (северный)",
        "СВ (северо-восточный)",
        "В (восточный)",
        "ЮВ (юго-восточный)",
        "Ю (южный)",
        "ЮЗ (юго-западный)",
        "З (западный)",
        "СЗ (северо-западный)",
    ];
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
        const normalizedDeg = ((numeric % 360) + 360) % 360;
        const index = Math.round(normalizedDeg / 45) % 8;
        return mapByIndex[index];
    }
    const normalized = String(value).trim().toUpperCase();
    const map = {
        N: "С (северный)",
        NW: "СЗ (северо-западный)",
        W: "З (западный)",
        SW: "ЮЗ (юго-западный)",
        S: "Ю (южный)",
        SE: "ЮВ (юго-восточный)",
        E: "В (восточный)",
        NE: "СВ (северо-восточный)",
    };
    return map[normalized] || value;
}

export function formatWindDirectionShort(value) {
    if (value === null || value === undefined || value === "") return "-";
    const byIndex = ["С", "СВ", "В", "ЮВ", "Ю", "ЮЗ", "З", "СЗ"];
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
        const normalizedDeg = ((numeric % 360) + 360) % 360;
        const index = Math.round(normalizedDeg / 45) % 8;
        return byIndex[index];
    }
    const normalized = String(value).trim().toUpperCase();
    const map = { N: "С", NE: "СВ", E: "В", SE: "ЮВ", S: "Ю", SW: "ЮЗ", W: "З", NW: "СЗ" };
    return map[normalized] || normalized;
}

export function getWindArrowRotation(value) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return (((numeric + 180) % 360) + 360) % 360;
    const normalized = String(value || "").trim().toUpperCase();
    const map = { N: 180, NE: 225, E: 270, SE: 315, S: 0, SW: 45, W: 90, NW: 135 };
    return map[normalized] ?? 0;
}

export function getMoonPhaseEmoji(dayKey) {
    const date = new Date(`${dayKey}T12:00:00Z`);
    if (Number.isNaN(date.getTime())) return "🌙";
    const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14, 0);
    const synodicMonthMs = 29.530588853 * 24 * 60 * 60 * 1000;
    const ageMs = date.getTime() - knownNewMoon;
    let phase = ((ageMs % synodicMonthMs) + synodicMonthMs) % synodicMonthMs;
    phase /= synodicMonthMs;
    if (phase < 0.03 || phase > 0.97) return "🌑";
    if (phase < 0.22) return "🌒";
    if (phase < 0.28) return "🌓";
    if (phase < 0.47) return "🌔";
    if (phase < 0.53) return "🌕";
    if (phase < 0.72) return "🌖";
    if (phase < 0.78) return "🌗";
    return "🌘";
}

export function getDayHourFromTimestamp(timestamp) {
    const source = String(timestamp || "");
    const direct = source.match(/^(\d{4}-\d{2}-\d{2})T(\d{2})/);
    if (direct) {
        return { dayKey: direct[1], hourKey: direct[2] };
    }
    const date = new Date(source);
    if (Number.isNaN(date.getTime())) return null;
    const dayKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
    const hourKey = String(date.getUTCHours()).padStart(2, "0");
    return { dayKey, hourKey };
}

export function formatCloudinessText(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "переменная облачность";
    if (num <= 25) return "ясно";
    if (num <= 75) return "переменная облачность";
    return "облачно";
}

export function formatPrecipitationText(value) {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return "осадков нет";
    return `вероятность осадков ${num}%`;
}

export function toKmh(speed) {
    const num = Number(speed);
    if (!Number.isFinite(num)) return "-";
    return num.toFixed(1);
}

export function pickBestSlot(data) {
    const timeline = Array.isArray(data?.timeline) ? data.timeline : [];
    if (!timeline.length) return null;
    const bestDatetime = data?.bestWindow?.datetime;
    if (bestDatetime) {
        const exact = timeline.find((slot) => slot?.datetime === bestDatetime);
        if (exact) return exact;
    }
    return timeline.reduce((best, current) => {
        const bestScore = Number(best?.scorePercent ?? -1);
        const currentScore = Number(current?.scorePercent ?? -1);
        return currentScore > bestScore ? current : best;
    }, null);
}

export function pickWeatherIconKey(source) {
    const icon = source?.icon;
    if (icon && WEATHER_ICON_FILES[icon]) return icon;
    const precipitation = String(source?.precipitationType || source?.precipitation || "").toLowerCase();
    const cloud = String(source?.cloudCover ?? source?.cloudiness ?? "").toLowerCase();
    if (precipitation.includes("snow") || precipitation.includes("снег")) return "snow";
    if (precipitation.includes("hail") || precipitation.includes("град")) return "hail";
    if (precipitation.includes("лив") || precipitation.includes("heavy") || precipitation.includes("rain")) return "heavy-rain";
    if (precipitation.includes("дожд")) return "light-rain";
    if (cloud.includes("overcast") || cloud.includes("пасмур")) return "cloudy";
    if (cloud.includes("cloud")) return "partly-cloudy";
    return "partly-cloudy";
}
